import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { 
  getCharacterByIdAtom, 
  fetchCharacterByIdAtom,
  charactersLoadingAtom 
} from '../../../atoms/v3/characterAtoms';
import { Character } from '../../../types';

/**
 * 根据角色卡ID获取角色卡信息的Hook
 * 支持按需加载和缓存
 * 
 * @param characterId 角色卡ID
 * @returns { character: Character | null, isLoading: boolean }
 */
export const useCharacterById = (characterId?: string) => {
  const getCharacterById = useAtomValue(getCharacterByIdAtom);
  const fetchCharacterById = useSetAtom(fetchCharacterByIdAtom);
  const isGlobalLoading = useAtomValue(charactersLoadingAtom);
  
  const [character, setCharacter] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasTriedLoading, setHasTriedLoading] = useState(false);

  useEffect(() => {
    if (!characterId) {
      setCharacter(null);
      setIsLoading(false);
      setHasTriedLoading(false);
      return;
    }

    // 先尝试从缓存和已加载的列表中查找
    const cachedCharacter = getCharacterById(characterId);
    if (cachedCharacter) {
      setCharacter(cachedCharacter);
      setIsLoading(false);
      setHasTriedLoading(true);
      return;
    }

    // 如果没有找到且还没有尝试加载过，则触发加载
    if (!hasTriedLoading && !isGlobalLoading) {
      setIsLoading(true);
      setHasTriedLoading(true);
      
      fetchCharacterById(characterId)
        .then((loadedCharacter) => {
          setCharacter(loadedCharacter);
        })
        .catch((error) => {
          console.warn(`Failed to load character ${characterId}:`, error);
          setCharacter(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [characterId, getCharacterById, fetchCharacterById, isGlobalLoading, hasTriedLoading]);

  // 当角色卡ID变化时，重置状态
  useEffect(() => {
    setHasTriedLoading(false);
  }, [characterId]);

  return {
    character,
    isLoading: isLoading || isGlobalLoading
  };
};
